import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Typography,
  type TableColumnsType,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useReferenceListReasons } from '@/api/generated/endpoints/reference/reference';
import { useShipmentsTransition } from '@/api/generated/endpoints/shipments/shipments';
import type {
  Disposition,
  ShipmentDetail,
  ShipmentItemOut,
  ShipmentsTransitionBody,
} from '@/api/generated/model';
import { ApiProblem } from '@/api/http';
import { confirmAction } from '@/components/confirmAction';
import { QtyCell, QtyInput } from '@/components/qty';
import { problemMessage } from '@/i18n/problem';
import { useIsMobile } from '@/lib/useIsMobile';
import { useOutbox } from '@/offline/context';
import { isNetworkError } from '@/offline/network';
import { reasonLabel } from '@/i18n/reason';
import { acceptanceBalance } from '@/lib/acceptance';
import type { QtyValue } from '@/lib/qty';
import { invalidatePath } from '@/lib/queryCache';
import { qtyText } from '@/lib/units';

export type AcceptanceKind = 'accepted_full' | 'accepted_partial' | 'returned';

interface LineValues {
  returned: QtyValue;
  disposition?: Disposition;
}

interface Values {
  closed_at: Dayjs;
  reason_id?: number;
  comment?: string;
  disposition?: Disposition;
  /** Только у «Принято частично»: у других видов строк в форме нет. */
  lines?: Record<string, LineValues>;
}

/**
 * Приёмка клиентом (ТЗ): «Принято полностью», «Принято частично» (по строкам — сколько
 * не принято, живой баланс), «Возврат». Причина и комментарий обязательны для частичной
 * приёмки и возврата; что делать с товаром — по умолчанию из причины (T-114).
 */
export function AcceptanceModal({
  shipment,
  kind,
  onClose,
  onDone,
}: {
  shipment: ShipmentDetail;
  kind: AcceptanceKind | null;
  onClose: () => void;
  onDone: (s: ShipmentDetail) => void;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<Values>();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { modal, message } = App.useApp();
  const outbox = useOutbox();
  const mobile = useIsMobile();
  const reasons = useReferenceListReasons(
    { status: 'active' },
    { query: { enabled: kind !== null } },
  );
  const reasonId = Form.useWatch<number | undefined>('reason_id', form);
  const lineValues = Form.useWatch('lines', form) ?? {};
  const reason = reasons.data?.find((r) => r.id === reasonId);
  const partial = kind === 'accepted_partial';
  const balance = acceptanceBalance(
    shipment.items.map((i) => ({ shipped: i.qty, returned: lineValues[String(i.id)]?.returned })),
  );

  const transition = useShipmentsTransition({
    mutation: {
      onSuccess: async (s) => {
        await Promise.all([
          invalidatePath(queryClient, '/api/v1/shipments'),
          invalidatePath(queryClient, '/api/v1/stock'),
          invalidatePath(queryClient, '/api/v1/batches'),
        ]);
        close();
        onDone(s);
      },
      onError: async (e, { data }) => {
        if (isNetworkError(e)) {
          queueAcceptance(data);
          return;
        }
        if (e instanceof ApiProblem && e.code === 'version_conflict') {
          await invalidatePath(queryClient, '/api/v1/shipments');
          setError(t('shipment.versionConflict'));
          return;
        }
        setError(problemMessage(e));
      },
    },
  });

  /** Нет связи — приёмка клиентом ждёт в очереди (ADR-0009, T-182). */
  function queueAcceptance(data: ShipmentsTransitionBody) {
    outbox.markOffline();
    const status = t(`status.shipment.${data.to}.label`);
    outbox.enqueue({
      kind: 'transition',
      url: `/api/v1/shipments/${shipment.id}/transitions`,
      body: { ...data },
      label: t('offline.transitionLabel', { number: shipment.number, status }),
    });
    void message.warning(t('offline.queuedAcceptance', { number: shipment.number, status }));
    close();
  }

  function close() {
    setError(null);
    form.resetFields();
    onClose();
  }

  /** Причина выбрана — подставить «что делать с товаром» из неё (T-114). */
  function onReason(id: number) {
    const d = reasons.data?.find((r) => r.id === id)?.default_disposition;
    if (!d) return;
    form.setFieldValue('disposition', d);
    for (const item of shipment.items)
      form.setFieldValue(['lines', String(item.id), 'disposition'], d);
  }

  /** Что уйдёт в списание: по строке — её выбор, иначе выбор по отгрузке, иначе из причины. */
  function writeoffs(v: Values): string[] {
    const lines: Partial<Record<string, LineValues>> = v.lines ?? {};
    const fallback = v.disposition ?? reason?.default_disposition;
    if (kind === 'returned') {
      return shipment.items
        .filter((i) => (lines[String(i.id)]?.disposition ?? fallback) === 'writeoff')
        .map((i) => `${i.product.name}: ${qtyText(t, i.qty, i.product.base_uom)}`);
    }
    if (kind !== 'accepted_partial') return [];
    return shipment.items.flatMap((i) => {
      const line = lines[String(i.id)];
      const qty = line?.returned;
      if (typeof qty !== 'string' || !/[1-9]/.test(qty)) return [];
      if ((line?.disposition ?? fallback) !== 'writeoff') return [];
      return [`${i.product.name}: ${qtyText(t, qty, i.product.base_uom)}`];
    });
  }

  async function submit(v: Values) {
    if (!kind) return;
    const lost = writeoffs(v);
    if (
      lost.length > 0 &&
      !(await confirmAction(modal, {
        title: t('acceptance.confirmWriteoffTitle', { number: shipment.number }),
        details: lost,
        okText: t('acceptance.confirmWriteoff'),
        cancelText: t('common.cancel'),
      }))
    )
      return;
    setError(null);
    const base = {
      version: shipment.version,
      closed_at: v.closed_at.toISOString(),
    };
    const comment = v.comment?.trim() ?? '';
    let data: ShipmentsTransitionBody;
    if (kind === 'accepted_full') {
      data = { ...base, to: 'accepted_full', comment: comment || null };
    } else if (kind === 'returned') {
      data = {
        ...base,
        to: 'returned',
        reason_id: v.reason_id ?? 0,
        comment,
        disposition: v.disposition ?? null,
      };
    } else {
      if (balance.summary !== 'ok') return;
      data = {
        ...base,
        to: 'accepted_partial',
        reason_id: v.reason_id ?? 0,
        comment,
        lines: shipment.items
          .map((i) => ({ item: i, line: v.lines?.[String(i.id)] }))
          .filter(({ line }) => typeof line?.returned === 'string' && /[1-9]/.test(line.returned))
          .map(({ item, line }) => ({
            item_id: item.id,
            qty_returned: line?.returned as string,
            disposition: line?.disposition ?? null,
          })),
      };
    }
    if (!outbox.online) {
      queueAcceptance(data);
      return;
    }
    transition.mutate({ shipmentId: shipment.id, data });
  }

  const returnedField = (i: ShipmentItemOut, index: number) => (
    <Form.Item
      name={['lines', String(i.id), 'returned']}
      style={{ margin: 0 }}
      {...(balance.lines[index]?.over
        ? { validateStatus: 'error' as const, help: t('acceptance.overShipped') }
        : {})}
    >
      <QtyInput
        uom={i.product.base_uom}
        aria-label={`${t('acceptance.colReturned')} ${i.line_no}`}
      />
    </Form.Item>
  );
  const dispositionField = (i: ShipmentItemOut) => (
    <Form.Item name={['lines', String(i.id), 'disposition']} style={{ margin: 0 }}>
      <Select<Disposition>
        disabled={!lineValues[String(i.id)]?.returned}
        aria-label={`${t('acceptance.colDisposition')} ${i.line_no}`}
        options={[
          { value: 'restock', label: t('disposition.restock') },
          { value: 'writeoff', label: t('disposition.writeoff') },
        ]}
      />
    </Form.Item>
  );

  // Телефон (T-176): строка — карточкой сверху вниз, поля во всю ширину.
  const mobileColumns: TableColumnsType<ShipmentItemOut> = [
    {
      key: 'line',
      title: t('acceptance.colProduct'),
      render: (_, i, index) => (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Typography.Text strong>{i.product.name}</Typography.Text>
          <Typography.Text type="secondary">
            {t('acceptance.colShipped')}: <QtyCell value={i.qty} uom={i.product.base_uom} />
            {' · '}
            {t('acceptance.colAccepted')}:{' '}
            <QtyCell value={balance.lines[index]?.accepted} uom={i.product.base_uom} />
          </Typography.Text>
          <Typography.Text>{t('acceptance.colReturned')}</Typography.Text>
          {returnedField(i, index)}
          <Typography.Text>{t('acceptance.colDisposition')}</Typography.Text>
          {dispositionField(i)}
        </Space>
      ),
    },
  ];

  const columns: TableColumnsType<ShipmentItemOut> = [
    { key: 'product', title: t('acceptance.colProduct'), render: (_, i) => i.product.name },
    {
      key: 'shipped',
      title: t('acceptance.colShipped'),
      align: 'right',
      width: 120,
      render: (_, i) => <QtyCell value={i.qty} uom={i.product.base_uom} />,
    },
    {
      key: 'returned',
      title: t('acceptance.colReturned'),
      width: 170,
      render: (_, i, index) => returnedField(i, index),
    },
    {
      key: 'accepted',
      title: t('acceptance.colAccepted'),
      align: 'right',
      width: 120,
      render: (_, i, index) => (
        <QtyCell value={balance.lines[index]?.accepted} uom={i.product.base_uom} />
      ),
    },
    {
      key: 'disposition',
      title: t('acceptance.colDisposition'),
      width: 170,
      render: (_, i) => dispositionField(i),
    },
  ];

  return (
    <Modal
      title={kind ? t(`acceptance.${kind}`) : ''}
      open={kind !== null}
      onCancel={close}
      okText={t('acceptance.submit')}
      okButtonProps={{
        loading: transition.isPending,
        disabled: partial && balance.summary !== 'ok',
        danger: kind === 'returned',
      }}
      onOk={() => {
        form.submit();
      }}
      width={mobile ? '100%' : partial ? 900 : 560}
      {...(mobile ? { style: { top: 8, maxWidth: 'calc(100vw - 16px)' } } : {})}
      destroyOnHidden
    >
      {kind && (
        <Typography.Paragraph type="secondary">{t(`acceptance.${kind}Hint`)}</Typography.Paragraph>
      )}
      <Form<Values>
        form={form}
        layout="vertical"
        initialValues={{ closed_at: dayjs(), lines: {} }}
        onFinish={(v) => {
          void submit(v);
        }}
      >
        <Form.Item name="closed_at" label={t('acceptance.closedAt')}>
          <DatePicker
            showTime={{ format: 'HH:mm' }}
            format="DD.MM.YYYY HH:mm"
            allowClear={false}
            disabledDate={(d) =>
              d.isAfter(dayjs(), 'day') ||
              (shipment.shipped_at !== null && d.isBefore(dayjs(shipment.shipped_at), 'day'))
            }
          />
        </Form.Item>
        {kind !== 'accepted_full' && (
          <Form.Item
            name="reason_id"
            label={t('acceptance.reason')}
            rules={[{ required: true, message: t('acceptance.reasonRequired') }]}
          >
            <Select<number>
              loading={reasons.isPending}
              onChange={onReason}
              options={(reasons.data ?? []).map((r) => ({ value: r.id, label: reasonLabel(r) }))}
            />
          </Form.Item>
        )}
        {kind === 'returned' && (
          <Form.Item
            name="disposition"
            label={t('acceptance.disposition')}
            extra={t('acceptance.dispositionHint')}
          >
            <Radio.Group
              optionType="button"
              disabled={!reason}
              options={[
                { value: 'restock', label: t('disposition.restock') },
                { value: 'writeoff', label: t('disposition.writeoff') },
              ]}
            />
          </Form.Item>
        )}
        {partial && (
          <>
            <Table<ShipmentItemOut>
              rowKey="id"
              size="small"
              columns={mobile ? mobileColumns : columns}
              dataSource={shipment.items}
              pagination={false}
              style={{ marginBottom: 12 }}
            />
            {balance.summary === 'none' && (
              <Alert
                type="info"
                showIcon
                message={t('acceptance.noneReturned')}
                style={{ marginBottom: 12 }}
              />
            )}
            {balance.summary === 'all' && (
              <Alert
                type="warning"
                showIcon
                message={t('acceptance.allReturned')}
                style={{ marginBottom: 12 }}
              />
            )}
          </>
        )}
        <Form.Item
          name="comment"
          label={t('acceptance.comment')}
          rules={
            kind === 'accepted_full'
              ? []
              : [
                  {
                    required: true,
                    whitespace: true,
                    min: 3,
                    message: t('acceptance.commentRequired'),
                  },
                ]
          }
        >
          <Input.TextArea maxLength={2000} autoSize={{ minRows: 2, maxRows: 5 }} />
        </Form.Item>
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}
